import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, X } from 'lucide-react';

/**
 * Session Warning Component
 * Shows a warning banner when session is about to expire
 * Provides options to extend session or logout
 */
const SessionWarning = ({ minutesRemaining, onExtend, onLogout, onDismiss }) => {
  const [countdown, setCountdown] = useState(minutesRemaining * 60); // Convert to seconds
  const [dismissed, setDismissed] = useState(false);

  // Update countdown every second
  useEffect(() => {
    if (!minutesRemaining || minutesRemaining <= 0 || dismissed) {
      return;
    }

    setCountdown(minutesRemaining * 60);

    const timer = setInterval(() => {
      setCountdown(prev => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          clearInterval(timer);
          return 0;
        }
        return newValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [minutesRemaining, dismissed]);

  // Don't show if no minutes remaining, already dismissed, or time is up
  if (!minutesRemaining || minutesRemaining > 2 || dismissed || countdown <= 0) {
    return null;
  }

  const handleExtend = () => {
    setDismissed(false);
    if (onExtend) {
      onExtend();
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getWarningColor = () => {
    if (countdown <= 30) return 'bg-red-50 border-red-400';
    if (countdown <= 60) return 'bg-orange-50 border-orange-400';
    return 'bg-yellow-50 border-yellow-400';
  };

  const getIconColor = () => {
    if (countdown <= 30) return 'text-red-400';
    if (countdown <= 60) return 'text-orange-400';
    return 'text-yellow-400';
  };

  const getTextColor = () => {
    if (countdown <= 30) return 'text-red-800';
    if (countdown <= 60) return 'text-orange-800';
    return 'text-yellow-800';
  };

  const getButtonColor = () => {
    if (countdown <= 30) return 'bg-red-600 hover:bg-red-700';
    if (countdown <= 60) return 'bg-orange-600 hover:bg-orange-700';
    return 'bg-yellow-600 hover:bg-yellow-700';
  };

  return (
    <div className={`fixed top-4 right-4 left-4 md:left-auto md:w-96 z-50 ${getWarningColor()} border-l-4 rounded-lg shadow-2xl animate-in slide-in-from-top-5 duration-300`}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className={`h-6 w-6 ${getIconColor()}`} />
          </div>
          <div className="ml-3 flex-1">
            <h3 className={`text-sm font-medium ${getTextColor()}`}>
              Sessão Expirando em Breve
            </h3>
            <div className="mt-2">
              <p className={`text-sm ${getTextColor()}`}>
                Sua sessão expirará por inatividade em:
              </p>
              <div className="mt-2 flex items-center">
                <Clock className={`h-5 w-5 ${getIconColor()} mr-2`} />
                <span className={`text-2xl font-mono font-bold ${getTextColor()}`}>
                  {formatTime(countdown)}
                </span>
              </div>
              <p className={`mt-2 text-xs ${getTextColor()}`}>
                Clique em "Continuar Sessão" para permanecer conectado, ou você será desconectado automaticamente.
              </p>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleExtend}
                className={`flex-1 px-3 py-2 ${getButtonColor()} text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 transition-colors`}
              >
                Continuar Sessão
              </button>
              <button
                onClick={onLogout}
                className="flex-1 px-3 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Sair Agora
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className={`ml-3 flex-shrink-0 inline-flex ${getTextColor()} hover:opacity-75 focus:outline-none`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionWarning;
